import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Blockchain Redux experiment, part 2",
  "description": "",
  "published": "2018-01-04T00:00:00.000Z",
  "redirect_from": ["/blog/blockchain-redux-experiment-part-2/swizec/8055"],
  "categories": "Livecoding, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/blockchain-redux"
      }}>{`blockchain-redux`}</a>{` experiment continues. State is now shared and preserved.`}</p>
    <lite-youtube {...{
      "videoid": "CREqFkX_r1o",
      "videostartat": "0"
    }}></lite-youtube>
    <lite-youtube {...{
      "videoid": "EW8uJ8Z2pYA",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`When I last wrote about this `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/blockchain-backed-redux-clone-105-lines-code/swizec/7980"
      }}>{`blockchain-backed Redux clone in 105 lines of code`}</a>{`, it could:`}</p>
    <ul>
      <li parentName="ul">{`build a local blockchain`}</li>
      <li parentName="ul">{`add blocks on every action `}<inlineCode parentName="li">{`dispatch`}</inlineCode></li>
      <li parentName="ul">{`verify the chain is valid`}</li>
      <li parentName="ul">{`replace the chain with a longer chain`}</li>
    </ul>
    <p>{`All the basic building blocks. But it didn't quite have feature parity with `}<a parentName="p" {...{
        "href": "https://github.com/reactjs/redux"
      }}>{`the real redux`}</a>{`, and it lacked some way to share the blockchain between nodes. You started a new chain with every call to `}<inlineCode parentName="p">{`createStore()`}</inlineCode>{`.`}</p>
    <p>{`That has now been fixed.`}</p>
    <h2 {...{
      "id": "feature-parity-with-redux"
    }}>{`Feature parity with Redux`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/01/carbon-feature-parity.png",
        "alt": "Basically this is it, this is Redux"
      }}></img></p>
    <p>{`We `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=CREqFkX%5C_r1o"
      }}>{`reached Redux feature parity`}</a>{`… I think. We added the ability to subscribe to changes and to use middleware.`}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/Swizec/blockchain-redux"
      }}>{`blockchain-redux`}</a>{` looks and feels the same as real Redux when you're using it, and I think that means it's got feature parity. In theory, you should be able to drop-in any redux plugin or middleware and it's gonna work.`}</p>
    <h3 {...{
      "id": "subscribing-to-changes"
    }}>{`Subscribing to changes`}</h3>
    <p>{`The concept here is simple:`}</p>
    <ol>
      <li parentName="ol">{`A method adds functions to an array of `}<inlineCode parentName="li">{`listeners`}</inlineCode></li>
      <li parentName="ol">{`Dispatch calls all of them`}</li>
    </ol>
    <p><img parentName="p" {...{
        "src": "/482c119ee84c855a7c5a53bb80edf0e6/media-krhB8ydCQiYZq-giphy.gif",
        "alt": null
      }}></img></p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`let listeners = [];

function subscribe(listener) {
  listeners.push(listener);

  return function unsubscribe() {
    listeners.splice(listeners.indexOf(listener), 1);
  };
}
`}</code></pre>
    <p>{`We keep a `}<inlineCode parentName="p">{`listeners`}</inlineCode>{` array. It starts as empty. Calling `}<inlineCode parentName="p">{`subscribe`}</inlineCode>{`, which is exposed as an API, adds your listener to the list. In `}<inlineCode parentName="p">{`return`}</inlineCode>{` you get a function that lets you unsubscribe.`}</p>
    <p>{`Using `}<inlineCode parentName="p">{`listeners.splice`}</inlineCode>{` to remove functions like that comes `}<a parentName="p" {...{
        "href": "https://github.com/reactjs/redux/blob/master/src/createStore.js#L136"
      }}>{`straight from Redux source`}</a>{`. It's probably faster than building a new list using `}<inlineCode parentName="p">{`.filter`}</inlineCode>{`.`}</p>
    <h3 {...{
      "id": "middleware-support"
    }}>{`Middleware support`}</h3>
    <p>{`Adding middleware support was trickier.`}</p>
    <p>{`Middleware, in essence, wraps the Redux store and changes how its functions work. You can think of it like a higher-order-component or a function decorator. Whichever makes more sense to you.`}</p>
    <p>{`Redux itself makes this easier with an `}<inlineCode parentName="p">{`applyMiddleware`}</inlineCode>{` function, but I didn't want to build that just yet. I don't think it's strictly necessary, but maybe that breaks feature parity. 🤔`}</p>
    <p>{`First part is to add a 3rd argument to `}<inlineCode parentName="p">{`createStore`}</inlineCode>{` – the `}<inlineCode parentName="p">{`enhancer`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function createStore(reducer, preloadedState, enhancer) {
    if (
        typeof preloadedState === "function" &&
        typeof enhancer === "undefined"
    ) {
        enhancer = preloadedState;
        preloadedState = undefined;
    }

    if (typeof enhancer === "function") {
        return enhancer(createStore)(reducer, preloadedState);
    }
`}</code></pre>
    <p>{`If `}<inlineCode parentName="p">{`enhancer`}</inlineCode>{` is a function, we pass the `}<inlineCode parentName="p">{`createstore`}</inlineCode>{` function to it and say, `}<em parentName="p">{`"Ok, you do this.”`}</em></p>
    <p>{`The enhancer, a middleware, then takes care of instantiating the store and changing its behavior. A simple middleware that prints out every call to `}<inlineCode parentName="p">{`dispatch`}</inlineCode>{` would look like this.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// a logging middleweare
export default function () {
  return (createStore) => (...args) => {
    const store = createStore(...args);
    const _dispatch = store.dispatch;

    function dispatch(action) {
      _dispatch(action);

      console.log("ACTION", action);
    }

    return Object.assign(store, {
      dispatch,
    });
  };
}
`}</code></pre>
    <p>{`We're using some functional concepts here to do our thing. Currying for the most part.`}</p>
    <p>{`Our logging middleware returns a function that takes `}<inlineCode parentName="p">{`createStore`}</inlineCode>{` as its argument. This in turn returns a function that takes arguments, usually `}<inlineCode parentName="p">{`reducer`}</inlineCode>{` and `}<inlineCode parentName="p">{`preloadedState`}</inlineCode>{`.`}</p>
    <p>{`It immediately calls `}<inlineCode parentName="p">{`createState`}</inlineCode>{` to build the store. This time without passing a middleware.`}</p>
    <p>{`Then our redux middleware overwrites the `}<inlineCode parentName="p">{`dispatch`}</inlineCode>{` function with its own function that prints actions as well as calls the original dispatch.`}</p>
    <p>{`In the end, we return the augmented store.`}</p>
    <p>{`I borrowed this approach from Redux's applyMiddleware function, and it works great. Where I think my approach might suffer is applying multiple middlewares.`}</p>
    <p>{`But we can solve that problem when we get to it 😇`}</p>
    <h2 {...{
      "id": "sharing-the-chain-between-nodes"
    }}>{`Sharing the chain between nodes`}</h2>
    <p>{`We went to all that trouble of supporting middleware so that we can build different strategies for sharing our blockchain. blockchain-redux itself should focus on building the blockchain, dispatching actions, and keeping state.`}</p>
    <p>{`How that gets shared between nodes lives in a separate layer.`}</p>
    <p>{`For now, that layer is Firebase. This gives us an easy backend to work against and make sure nodes are communicating. Great way to iron out kinks, but it makes our blockchain centralized.`}</p>
    <p>{`We'll solve the centralization problem next.`}</p>
    <p>{`Here's how the Firebase middleware works 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/01/carbon-firebase-blockchain-sharing.png",
        "alt": "Sharing the chain between nodes using Firebase"
      }}></img></p>
    <p>{`Our middleware exports a function that takes an initialized FirebaseApp as its argument. This made it easier to have multiple instances of the store sharing the same Firebase when testing.`}</p>
    <p>{`Just like before, the only function it augments is `}<inlineCode parentName="p">{`dispatch`}</inlineCode>{`. It now tries to save new blocks to Firebase.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function dispatch(action) {
  _dispatch(action);

  const newBlock = store.getLastBlock();
  return saveBlock(newBlock);
}
`}</code></pre>
    <p>{`Dispatch the action, get latest block from the store, save block to firebase. 👌`}</p>
    <p>{`Saving that block is fraught with some difficulty, however.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function saveBlock(block) {
  block._data = block._data || {};

  return db
    .ref(\`blockchain/\${block.index}\`)
    .once("value")
    .then((snapshot) => {
      if (!snapshot.exists()) {
        db.ref(\`blockchain/\${block.index}\`).set(block);
        return true;
      } else {
        // collision resolution?
        return false;
      }
    });
}
`}</code></pre>
    <p>{`We save blocks at `}<inlineCode parentName="p">{`blockchain/<index>`}</inlineCode>{`. On every save, we check if the blocks already exists. If it doesn’t, we save the block and everyone is happy.`}</p>
    <p>{`However, if somebody else already created the same block successfully, we're in trouble. Right now, the middleware silently drops the save, and that's bad.`}</p>
    <p>{`I think the easiest solution will be to sync with the Firebase blockchain before dispatching an action and making a new block. Or we could dispatch the action and run a save-dispatch-save cycle until it succeeds.`}</p>
    <p>{`Neither of those approaches is very optimal from a network utilization perspective. Not sure yet what to do here.`}</p>
    <h3 {...{
      "id": "initing-the-store-from-blockchain"
    }}>{`Initing the store from blockchain`}</h3>
    <p>{`Another interesting problem was initializing our store from the blockchain. How do you ensure you have the whole chain before building new blocks?`}</p>
    <p>{`One approach I found was to read the whole thing from Firebase and replacing the local stub chain with the big one.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function initFromFirebase() {
  return db
    .ref("blockchain")
    .orderByKey()
    .once("value")
    .then((snapshot) => snapshot.val())
    .then((blockchain) => {
      blockchain = Object.values(blockchain).map((block) => {
        block.data = block._data ? JSON.parse(block._data) : {};
        return block;
      });

      store.replaceChain(blockchain);

      return Object.assign(store, {
        dispatch,
      });
    });
}

return Promise.all(store._blockchain.map(saveBlock)).then(initFromFirebase);
`}</code></pre>
    <p><inlineCode parentName="p">{`initFromFirebase`}</inlineCode>{` reads our central blockchain from Firebase, goes through the blocks and makes sure `}<inlineCode parentName="p">{`data`}</inlineCode>{` is in the right format, then runs `}<inlineCode parentName="p">{`replaceChain`}</inlineCode>{`.`}</p>
    <p>{`You'll notice this is now where we return the Redux store. That means `}<inlineCode parentName="p">{`createStore`}</inlineCode>{` is asynchronous and wreaks havoc on just about everything we hold dear about using Redux.`}</p>
    <p>{`But I'm not sure how to fix that. We need to have the chain before dispatching our first function. Maybe a waiting mechanism in dispatch 🤔`}</p>
    <p>{`Oh, and that last part? That's so we create the blockchain stub on Firebase in case it doesn't exist yet. Goes through blocks in the default chain and makes sure they exist on Firebase.`}</p>
    <h2 {...{
      "id": "what-next"
    }}>{`What next`}</h2>
    <p>{`This is still a rough experiment. The blockchain implementation works, it communicates through Firebase so it's distributed, but it has trouble with conflict resolution, has a centralized source of truth, and is very network and memory inefficient.`}</p>
    <p>{`All of that is bad.`}</p>
    <p>{`I want to move towards using Firebase as just a communication protocol between nodes so instead of saving the whole blockchain there, we just announce new blocks and other nodes pick them up. That would be more efficient.`}</p>
    <p>{`I also want to figure out how to avoid saving the whole blockchain locally. But maybe that's fundamentally not possible 🤔`}</p>
    <p>{`Got suggestions? Yell `}<a parentName="p" {...{
        "href": "https://twitter.com/swizec"
      }}>{`at me on Twitter`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      